import React from 'react';
import { Package, Truck, MapPin } from 'lucide-react';
import { formatPrice } from '../../utils/utilityFunctions';

interface OrderItemsReviewProps {
  deliveryOption: any;
  cartItems?: any[]; // Optional, for total count fallback
}

const OrderItemsReview: React.FC<OrderItemsReviewProps> = ({ deliveryOption, cartItems }) => {
  if (!deliveryOption || !deliveryOption.details) return null;

  // Create a map of cart items by variant_id for easy lookup
  const cartItemMap = new Map();
  cartItems?.forEach(item => {
    cartItemMap.set(item.product_variant_id, item);
  });
  
  return (
    <>
  <h3 className="text-xs sm:text-sm font-bold text-gray-900 uppercase tracking-wider mb-4 flex items-center gap-2">
    <Package className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600" />
    Your Items ({cartItems?.reduce((sum, item) => sum + item.quantity, 0)})
  </h3>

  <div className="space-y-6">
    {deliveryOption.details.map((shipment: any, idx: number) => (
      <div key={idx} className="border-l-4 border-emerald-200 pl-3 sm:pl-4">
        {/* Shipment Header - Stack on mobile */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
          <div className="flex items-center gap-2">
            <Truck className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <span className="text-xs sm:text-sm font-medium text-gray-700">
              {deliveryOption.type === 'economic' && shipment.consolidated 
                ? `Shipping Together` 
                : `Shipment ${idx + 1}`}
            </span>
          </div>
          <span className="text-xs sm:text-sm font-medium text-gray-900">
            Shipping: {formatPrice(shipment.fee)}
          </span>
        </div>

        {/* Items List */}
        <div className="space-y-4">
          {shipment.items?.map((item: any, itemIdx: number) => {
            const cartItem = cartItemMap.get(item.variant_id);
            if (!cartItem) return null;
            console.log('Rendering item:', item, cartItem);
            return (
              <div key={itemIdx} className="flex gap-3 sm:gap-4 py-3 border-b border-gray-100 last:border-0">
                {/* Product Image - smaller on mobile */}
                <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 border border-gray-200">
                  {cartItem.image_url ? (
                    <img 
                      src={cartItem.image_url} 
                      alt={cartItem.product_title} 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/96x96?text=Product';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                      <Package className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
                    </div>
                  )}
                </div>
                
                {/* Product Details - Better mobile layout */}
                <div className="flex-1 min-w-0">
                  {/* Title and price row - stacked on mobile */}
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-1">
                    <p className="text-sm sm:text-base font-semibold text-gray-900 break-words pr-2">
                      {cartItem.product_title}
                    </p>
                    <div className="text-right sm:text-left">
                      <p className="text-sm sm:text-base font-bold text-gray-900 whitespace-nowrap">
                        {formatPrice(cartItem.price)}
                      </p>
                      {item.quantity > 1 && (
                        <p className="text-xs text-gray-500 whitespace-nowrap">
                          {formatPrice(cartItem.price * item.quantity)} total
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Variant */}
                  {cartItem.variant_name && (
                    <p className="text-xs text-gray-600 mt-1 break-words">
                      Variant: {cartItem.variant_name}
                    </p>
                  )}
                  
                  {/* Original warehouse for split shipments */}
                  {item.original_warehouse && !shipment.consolidated && (
                    <p className="text-xs text-gray-500 mt-1 break-words">
                      📦 Ships from: {item.original_warehouse}
                    </p>
                  )}
                  
                  {/* Quantity and seller - row on mobile */}
                  <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mt-2">
                    <span>Qty: {item.quantity}</span>
                    <span>•</span>
                    <span className="break-words">Sold by: {item.vendor_name}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    ))}
  </div>
</>
  );
};

export default OrderItemsReview;